import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  signal,
  viewChildren,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimationService } from 'src/app/services/animation/animation.service';
import { CursorService } from 'src/app/services/cursor_animation/cursor.service';
import { LoaderService } from 'src/app/services/loader/loader.service';

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HomePageComponent implements OnInit, OnDestroy {
  private cursorService = inject(CursorService);
  private loaderService = inject(LoaderService);
  private animate = inject(AnimationService);

  textContent = viewChildren<ElementRef>('textContent');
  isScrolled$ = signal(false);
  typetext = signal(
    'Our greatest inspiration is to transform African cities—one community and one project at a time.'
  );
  counter = signal(-1);
  isLoading = signal(false);
  opacity = signal(0);
  private typingInterval?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    setTimeout(() => {
      this.slideTextUp();
      this.slideTextLeft();
      const elements = this.textContent().map((el) => el.nativeElement);
      this.animate.fadeInElementsImmediate(elements, 0.05);
    }, 1000);

    gsap.registerPlugin(ScrollTrigger);
    this.loaderService.showLoader();
    this.typeJs();

    setTimeout(() => {
      this.isLoading.set(true);
      // After data loading is complete, hide the loader
      this.loaderService.hideLoader();
    }, 1000);
  }

  ngAfterViewInit(): void {
    this.slideTextUp();
  }

  onMouseEnter(): void {
    this.cursorService.hoverElement();
  }

  onMouseLeave(): void {
    this.cursorService.unhoverElement();
  }

  onMouseColorChange(color: string) {
    this.cursorService.changeCursorColor(color);
  }
  onChangeColorBack(color: string) {
    this.cursorService.changeCursorColorBack(color);
  }

  get loaderServiceValue() {
    return this.loaderService;
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: Event): void {
    const navElement = document.querySelector('nav');
    const companyLogo = document.getElementById(
      'companyLogo'
    ) as HTMLImageElement;
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    const menuBtn = document.getElementById('menuBtn');

    if (navElement) {
      this.opacity.set(Math.min(scrollPosition / 500, 1));

      if (scrollPosition > 200) {
        navElement.style.backgroundColor = '#fff';
        this.isScrolled$.set(true);

        if (companyLogo && menuBtn) {
          // companyLogo.style.opacity = '1'
          menuBtn.style.color = '#000';
        }
      } else {
        navElement.style.backgroundColor = 'transparent';
        this.isScrolled$.set(false);
        if (companyLogo && menuBtn) {
          // companyLogo.style.opacity = '0'
          companyLogo.src = 'assets/imgs/zumera-logo-red.png';
          menuBtn.style.color = '#fff';
        }
      }
    }
  }

  slideTextUp() {
    const contents = gsap.utils.toArray('.content');
    contents.forEach((content: any) => {
      gsap.to(content, {
        opacity: 1,
        y: 0,
        duration: 0.3,
        ease: 'power2.out',
        stagger: 0.1,
        transformOrigin: 'top',
        onComplete: () => {
          gsap.set(content, { opacity: 1 });
        },
        scrollTrigger: {
          trigger: content,
          start: 'top 100%',
          end: 'top 50%',
          toggleActions: 'play none none reverse',
          fastScrollEnd: true,
          invalidateOnRefresh: true,
        },
      });
    });
  }
  slideTextLeft() {
    gsap.to('.content-left', {
      opacity: 1,
      x: 0,
      duration: 0.3,
      ease: 'power2.out',
      stagger: 0.1,
      transformOrigin: 'left',
      onComplete: () => {
        gsap.set('.content-left', { opacity: 1 });
      },
      scrollTrigger: {
        trigger: '.content-left',
        toggleActions: 'play none none reverse',
        start: 'top 100%',
        end: 'top 50%',
        fastScrollEnd: true,
        invalidateOnRefresh: true,
      },
    });
  }

  typeJs(): void {
    const initialText = 'Our greatest inspiration is to transform African cities—one community and one project at a time.';
    const typeNext = () => {
      const currentCounter = this.counter();
      const currentText = this.typetext();

      if (currentCounter < initialText.length) {
        this.counter.update(c => c + 1);
        this.typetext.set(initialText.slice(0, this.counter() + 1));
        this.typingInterval = setTimeout(typeNext, 50);
      } else if (currentText.length === initialText.length) {
        setTimeout(() => {
          this.counter.set(-1);
          this.typingInterval = setTimeout(typeNext, 50);
        }, 1500);
      } else {
        this.typetext.set(initialText.slice(0, -1));
        if (this.typetext().length == 0) {
          setTimeout(() => {
            this.counter.set(-1);
            this.typingInterval = setTimeout(typeNext, 50);
          }, 3000);
        } else {
          this.typingInterval = setTimeout(typeNext, 50);
        }
      }
    };
    this.typingInterval = setTimeout(typeNext, 50);
  }

  ngOnDestroy(): void {
    if (this.typingInterval) {
      clearTimeout(this.typingInterval);
    }
  }
}
